import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link as RouterLink, useOutletContext } from "react-router-dom";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Alert,
  Button,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import QuizIcon from "@mui/icons-material/Quiz";
import WarningIcon from "@mui/icons-material/Warning";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import EventIcon from "@mui/icons-material/Event";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InboxIcon from "@mui/icons-material/Inbox";
import RefreshIcon from "@mui/icons-material/Refresh";
import DashboardIcon from "@mui/icons-material/Dashboard";
import api from "../../api";
import AnnouncementsList from "../../components/AnnouncementsList";
import TutorPanel from "./components/TutorPanel";
import TutorStatCard from "./components/TutorStatCard";
import TutorStatusBreakdown from "./components/TutorStatusBreakdown";
import TutorTeachingWeek from "./components/TutorTeachingWeek";
import TutorGroupsList from "./components/TutorGroupsList";
import {
  classStatusChip,
  resourceChipFor,
  recordingStatusChip,
} from "./tutorStatusChips";
import { getTutorPermissionsFromStorage } from "../../utils/tutorPermissions";

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

/** Pastel badge per KPI. The hue is carried by the 48px circle, never by the card itself. */
const KPI = [
  { key: "courses_count", label: "Courses", to: "/tutor/courses", bg: "#ede9fe", fg: "#7c3aed", icon: <SchoolIcon />, permission: "tutor-portal-courses" },
  { key: "students_count", label: "Students", to: "/tutor/students", bg: "#dbeafe", fg: "#2563eb", icon: <PersonIcon />, permission: "tutor-portal-students" },
  { key: "scheduled_classes_count", label: "Scheduled classes", to: "/tutor/classes?tab=2", bg: "#fce7f3", fg: "#db2777", icon: <VideocamIcon />, permission: "tutor-portal-classes" },
  { key: "assignments_count", label: "Assignments", to: "/tutor/assignments", bg: "#fef3c7", fg: "#d97706", icon: <AssignmentIcon />, permission: "tutor-portal-assignments" },
  { key: "timeslots_count", label: "Timeslots", to: "/tutor/timeslots", bg: "#dcfce7", fg: "#16a34a", icon: <AccessTimeIcon />, permission: "tutor-portal-timeslots" },
  { key: "tests_count", label: "Worksheets", to: "/tutor/tests", bg: "#cffafe", fg: "#0891b2", icon: <QuizIcon />, permission: "tutor-portal-tests" },
];

const QUICK_ACTIONS = [
  { label: "Start class", to: "/tutor/classes", bg: "#fce7f3", fg: "#db2777", icon: <VideocamIcon sx={{ fontSize: 28 }} /> },
  { label: "Schedule", to: "/tutor/classes/schedule", bg: "#ede9fe", fg: "#7c3aed", icon: <EventIcon sx={{ fontSize: 28 }} /> },
  { label: "Upload content", to: "/tutor/resources", bg: "#fff7ed", fg: "#ea580c", icon: <UploadFileIcon sx={{ fontSize: 28 }} /> },
  { label: "Recordings", to: "/tutor/recordings", bg: "#dcfce7", fg: "#16a34a", icon: <VideoLibraryIcon sx={{ fontSize: 28 }} /> },
  { label: "My students", to: "/tutor/students", bg: "#dbeafe", fg: "#2563eb", icon: <PeopleIcon sx={{ fontSize: 28 }} /> },
  { label: "Notifications", to: "/tutor/notifications", bg: "#cffafe", fg: "#0891b2", icon: <InboxIcon sx={{ fontSize: 28 }} /> },
];

/**
 * Tutor dashboard.
 *
 * Beyond the six counts from `tutor/dashboard`, the status panels are derived client-side from
 * endpoints the panel already has — `tutor/classrooms`, `tutor/resources`, `tutor/recordings`,
 * `tutor/groups` and `tutor/timeslots`. Nothing here needs a backend change, and each of those five
 * is optional: they go through `Promise.allSettled` and a panel simply does not render when its
 * request fails, which is also how a tutor without the matching `tutor-portal-*` permission sees
 * the page.
 *
 * Caveat worth knowing when a panel looks empty: the counts and timeslots are scoped by the
 * `course_tutor` pivot, while Groups is scoped by `course_plan_staff`. A tutor made lead of a group
 * but never added to `course_tutor` gets zeroes above and a populated Groups panel below.
 */
const TutorDashboard = () => {
  const { tutorPortalPermissions: outletPermissions } = useOutletContext() ?? {};
  const tutorPortalPermissions =
    outletPermissions !== undefined ? outletPermissions : getTutorPermissionsFromStorage();

  const visibleKpis = useMemo(() => {
    // null = permissions not synced yet (legacy open); otherwise match sidebar gates.
    const relaxed = tutorPortalPermissions === null;
    const has = (p) => relaxed || tutorPortalPermissions.includes(p);
    return KPI.filter((k) => !k.permission || has(k.permission));
  }, [tutorPortalPermissions]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [urgentAnnouncements, setUrgentAnnouncements] = useState([]);

  // null means "we could not read this", which is different from an empty list.
  const [classrooms, setClassrooms] = useState(null);
  const [resources, setResources] = useState(null);
  const [recordings, setRecordings] = useState(null);
  const [groups, setGroups] = useState(null);
  const [slots, setSlots] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [dash, cls, res, rec, grp, slt] = await Promise.allSettled([
      api.get("tutor/dashboard"),
      api.get("tutor/classrooms"),
      api.get("tutor/resources"),
      api.get("tutor/recordings"),
      api.get("tutor/groups"),
      api.get("tutor/timeslots"),
    ]);

    if (dash.status === "fulfilled" && dash.value.data?.success) {
      setData(dash.value.data.data);
    } else {
      setError(
        dash.status === "rejected"
          ? dash.reason?.response?.data?.message || "Failed to load dashboard"
          : "Failed to load dashboard"
      );
    }

    const listOf = (r) =>
      r.status === "fulfilled" && Array.isArray(r.value.data?.data) ? r.value.data.data : null;
    setClassrooms(listOf(cls));
    setResources(listOf(res));
    setRecordings(listOf(rec));
    setGroups(listOf(grp));
    setSlots(listOf(slt));
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const fetchUrgentAnnouncements = async () => {
      try {
        const response = await api.get("tutor/announcements", { params: { per_page: 100 } });
        const raw = response.data?.data;
        const list = Array.isArray(raw) ? raw : (raw?.data ? raw.data : []);
        const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000);
        const urgent = list.filter((a) => {
          if ((a.priority_level || "").toLowerCase() !== "urgent") return false;
          const created = a.created_at ? new Date(a.created_at) : null;
          return created && created >= cutoff;
        });
        setUrgentAnnouncements(urgent);
      } catch (err) {
        setUrgentAnnouncements([]);
      }
    };
    fetchUrgentAnnouncements();
  }, []);

  const classSegments = useMemo(() => {
    if (!classrooms) return null;
    return ["pending", "ongoing", "ended"].map((key) => ({
      label: classStatusChip[key].label,
      color: classStatusChip[key].color,
      value: classrooms.filter((c) => c.status === key).length,
    }));
  }, [classrooms]);

  const resourceSegments = useMemo(() => {
    if (!resources) return null;
    const byLabel = new Map();
    resources.forEach((r) => {
      const chip = resourceChipFor(r);
      const entry = byLabel.get(chip.label) || { label: chip.label, color: chip.color, value: 0 };
      entry.value += 1;
      byLabel.set(chip.label, entry);
    });
    return [...byLabel.values()].sort((a, b) => b.value - a.value);
  }, [resources]);

  const recordingSegments = useMemo(() => {
    if (!recordings) return null;
    return ["ready", "processing", "uploading", "failed"]
      .map((key) => ({
        label: recordingStatusChip[key].label,
        color: recordingStatusChip[key].color,
        value: recordings.filter((r) => r.status === key).length,
      }))
      .filter((s) => s.value > 0 || s.label === recordingStatusChip.ready.label);
  }, [recordings]);

  /** Finished group classes with nothing uploaded — the same rule the Recordings page applies. */
  const awaitingUpload = useMemo(() => {
    if (!classrooms || !recordings) return 0;
    const recorded = new Set(recordings.map((r) => r.class?.id).filter(Boolean));
    return classrooms.filter((c) => c.group?.id && c.status === "ended" && !recorded.has(c.id)).length;
  }, [classrooms, recordings]);

  const nextClasses = useMemo(() => {
    if (!classrooms) return null;
    return classrooms
      .filter((c) => c.status === "pending" || c.status === "ongoing")
      .sort((a, b) => new Date(a.start_time || 0) - new Date(b.start_time || 0))
      .slice(0, 5);
  }, [classrooms]);

  const d = data || {};

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "flex-start" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <DashboardIcon color="primary" />
            <Typography variant="h5" fontWeight={700}>
              Dashboard
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700 }}>
            Overview of your teaching this term
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={load}
          disabled={loading}
          variant="outlined"
          sx={{
            bgcolor: "white",
            borderColor: "#e5e7eb",
            color: "text.primary",
            borderRadius: 2,
            height: 40,
            alignSelf: { xs: "flex-start", sm: "auto" },
            "&:hover": { bgcolor: "#f9fafb", borderColor: "#d1d5db" },
          }}
        >
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {urgentAnnouncements.length > 0 && (
        <Alert
          severity="error"
          icon={<WarningIcon fontSize="inherit" />}
          sx={{ mb: 2, "& .MuiAlert-message": { width: "100%" } }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {urgentAnnouncements.map((ann) => (
              <Box
                key={ann.id}
                component={RouterLink}
                to="/tutor/announcements"
                sx={{
                  py: 1,
                  px: 1.5,
                  bgcolor: "rgba(255,255,255,0.2)",
                  borderRadius: 1,
                  borderLeft: "4px solid #d32f2f",
                  textDecoration: "none",
                  color: "inherit",
                  cursor: "pointer",
                  display: "block",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  {ann.title}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                  {ann.message?.length > 200 ? `${ann.message.slice(0, 200)}…` : ann.message}
                </Typography>
              </Box>
            ))}
          </Box>
        </Alert>
      )}

      <AnnouncementsList role="tutor" />

      {/* KPI row — only tiles the tutor is allowed to open; fill full width evenly */}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm:
              visibleKpis.length <= 1
                ? "1fr"
                : visibleKpis.length === 2
                  ? "repeat(2, minmax(0, 1fr))"
                  : "repeat(3, minmax(0, 1fr))",
            md: `repeat(${Math.max(1, visibleKpis.length)}, minmax(0, 1fr))`,
          },
        }}
      >
        {visibleKpis.map((k) => (
          <TutorStatCard
            key={k.key}
            label={k.label}
            value={d[k.key] ?? 0}
            icon={k.icon}
            bg={k.bg}
            fg={k.fg}
            to={k.to}
            loading={loading}
          />
        ))}
      </Box>

      {/* Status row */}
      <Grid container spacing={2} sx={{ mt: 0 }}>
        {loading && (
          <>
            {[0, 1, 2].map((n) => (
              <Grid item xs={12} md={4} key={n}>
                <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </>
        )}

        {!loading && classSegments && (
          <Grid item xs={12} md={4}>
            <TutorPanel
              title="Class status"
              hint="Every class you host, by lifecycle state. Counted from your Classes page."
              action={
                <Button size="small" component={RouterLink} to="/tutor/classes?tab=2">
                  View all
                </Button>
              }
            >
              <TutorStatusBreakdown segments={classSegments} centreLabel="classes" />
            </TutorPanel>
          </Grid>
        )}

        {!loading && resourceSegments && (
          <Grid item xs={12} md={4}>
            <TutorPanel
              title="My content"
              hint="Where each of your uploads has reached. A file is visible to students only after both a lead tutor and an admin approve it."
              action={
                <Button size="small" component={RouterLink} to="/tutor/resources">
                  View all
                </Button>
              }
            >
              {resourceSegments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  You have not uploaded any content yet.
                </Typography>
              ) : (
                <TutorStatusBreakdown segments={resourceSegments} donut={false} />
              )}
            </TutorPanel>
          </Grid>
        )}

        {!loading && recordingSegments && (
          <Grid item xs={12} md={4}>
            <TutorPanel
              title="Recordings"
              hint="Uploads go straight to Cloudflare Stream; students are notified once processing finishes."
              action={
                <Button size="small" component={RouterLink} to="/tutor/recordings">
                  View all
                </Button>
              }
            >
              <TutorStatusBreakdown segments={recordingSegments} donut={false} />
              <Box sx={{ flexGrow: 1 }} />
              {awaitingUpload > 0 && (
                <Alert
                  severity="warning"
                  sx={{ mt: 2, py: 0.5 }}
                  action={
                    <Button size="small" component={RouterLink} to="/tutor/recordings">
                      Upload
                    </Button>
                  }
                >
                  {awaitingUpload} finished {awaitingUpload === 1 ? "class has" : "classes have"} no
                  recording yet.
                </Alert>
              )}
            </TutorPanel>
          </Grid>
        )}
      </Grid>

      {/* Detail row */}
      <Grid container spacing={2} sx={{ mt: 0 }}>
        {!loading && nextClasses && (
          <Grid item xs={12} md={8}>
            <TutorPanel
              title="Next classes"
              action={
                <Button size="small" component={RouterLink} to="/tutor/classes?tab=2">
                  View all
                </Button>
              }
            >
              {nextClasses.length === 0 ? (
                <Stack alignItems="center" spacing={1} sx={{ py: 4, textAlign: "center" }}>
                  <EventIcon sx={{ fontSize: 40, color: "rgba(0,0,0,0.26)" }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    No upcoming classes
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Schedule one and it will appear here.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    component={RouterLink}
                    to="/tutor/classes/schedule"
                    startIcon={<EventIcon />}
                  >
                    Schedule a class
                  </Button>
                </Stack>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Class</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Group</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Starts</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nextClasses.map((c) => {
                      const chip = classStatusChip[c.status] || classStatusChip.pending;
                      return (
                        <TableRow key={c.id} hover>
                          <TableCell>{c.name}</TableCell>
                          <TableCell>
                            {c.group?.name ? (
                              <Chip size="small" label={c.group.name} sx={{ fontWeight: 600 }} />
                            ) : (
                              <Chip size="small" variant="outlined" label="Whole course" />
                            )}
                          </TableCell>
                          <TableCell>{c.subject?.name ?? "—"}</TableCell>
                          <TableCell>{formatDateTime(c.start_time)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={chip.label}
                              sx={{ bgcolor: `${chip.color}20`, color: chip.color, fontWeight: 600 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </TutorPanel>
          </Grid>
        )}

        {!loading && (
          <Grid item xs={12} md={nextClasses ? 4 : 12}>
            <TutorPanel title="Quick actions">
              <Grid container spacing={1}>
                {QUICK_ACTIONS.map((a) => (
                  <Grid item xs={4} key={a.label}>
                    <Box
                      component={RouterLink}
                      to={a.to}
                      sx={{
                        bgcolor: a.bg,
                        color: a.fg,
                        borderRadius: 2,
                        p: 1.2,
                        height: "100%",
                        textDecoration: "none",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        transition: "transform 0.15s ease, box-shadow 0.15s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      {a.icon}
                      <Typography
                        variant="caption"
                        mt={0.5}
                        fontWeight={600}
                        sx={{ color: a.fg, lineHeight: 1.2 }}
                      >
                        {a.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </TutorPanel>
          </Grid>
        )}
      </Grid>

      {/* Schedule row */}
      <Grid container spacing={2} sx={{ mt: 0 }}>
        {!loading && slots && (
          <Grid item xs={12} md={6}>
            <TutorPanel
              title="My teaching week"
              hint="Your recurring class times, from the time slots an admin set on your courses. Today comes first."
              action={
                <Button size="small" component={RouterLink} to="/tutor/timeslots">
                  View all
                </Button>
              }
            >
              <TutorTeachingWeek slots={slots} />
            </TutorPanel>
          </Grid>
        )}

        {!loading && groups && (
          <Grid item xs={12} md={slots ? 6 : 12}>
            <TutorPanel
              title="My groups"
              hint="Groups you lead or assist on. Student numbers count everyone enrolled, including enrolments made before groups existed."
              action={
                <Button size="small" component={RouterLink} to="/tutor/groups">
                  View all
                </Button>
              }
            >
              <TutorGroupsList groups={groups} />
            </TutorPanel>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default TutorDashboard;
